import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import type { CalendarExternalEvent } from '@scrambl/shared'
import { calendarApi } from '@/lib/api/calendar.api'
import { useAuth } from '@/store/auth.store'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useUserConnections } from '@/hooks/useIntegrations'
import { isTauri } from '@/lib/utils/tauri'

const CALENDAR_TYPES = ['GOOGLE_CALENDAR', 'OUTLOOK_CALENDAR']

const REFETCH_INTERVAL_MS = 60 * 60 * 1000 // 1 hour, per requirement

function startOfToday(): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

function endOfToday(): Date {
  const d = new Date()
  d.setHours(23, 59, 59, 999)
  return d
}

/**
 * A calendar event we'd offer to record: a timed video call that the current
 * user hasn't declined. Timing (about-to-start / about-to-end) is evaluated by
 * the consumer, not here, since the underlying query is cached for an hour.
 */
export interface RecordableMeeting {
  id: string
  title: string
  start: Date
  end: Date | null
  conferenceUrl: string
  conferenceProvider?: string
  /** Invitees — passed to the recorder so the AI can name speakers */
  attendees: { name?: string; email?: string }[]
}

/**
 * Loads today's external calendar events (Google now; Outlook when its sync
 * lands) and narrows them to recordable video meetings. Refreshes hourly + on
 * window focus so calendar changes through the day are picked up. Only enabled
 * on the desktop app, behind the MEETING_NOTES flag, and when the user has an
 * active calendar integration connected.
 */
export function useTodaysMeetings() {
  const { checkFeatureFlag } = useFeatureFlags()
  const userEmail = useAuth(s => s.user?.email)?.toLowerCase()

  const flagEnabled = isTauri() && checkFeatureFlag('MEETING_NOTES')

  const { data: connections } = useUserConnections({ enabled: flagEnabled })
  const hasCalendar = !!connections?.some(
    c => CALENDAR_TYPES.includes(String(c.type)) && String(c.status) === 'ACTIVE',
  )

  const enabled = flagEnabled && hasCalendar

  const query = useQuery({
    queryKey: ['todays-meetings', new Date().toISOString().slice(0, 10)],
    queryFn: () =>
      calendarApi.getEvents({
        startDate: startOfToday().toISOString(),
        endDate: endOfToday().toISOString(),
        includeTasks: false,
        includeExternalEvents: true,
      }),
    enabled,
    refetchInterval: enabled ? REFETCH_INTERVAL_MS : false,
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1000,
  })

  const meetings = useMemo<RecordableMeeting[]>(() => {
    const events = query.data?.externalEvents ?? []
    return events
      .filter((e): e is CalendarExternalEvent => !e.allDay && !!e.conferenceUrl)
      .filter(e => !isDeclinedByUser(e, userEmail))
      .map(e => ({
        id: e.id,
        title: e.title,
        start: new Date(e.start),
        end: e.end ? new Date(e.end) : null,
        conferenceUrl: e.conferenceUrl as string,
        conferenceProvider: e.conferenceProvider,
        attendees: (e.attendees ?? []).map(a => ({
          name: a.name,
          email: a.email,
        })),
      }))
  }, [query.data, userEmail])

  return { meetings, enabled, ...query }
}

function isDeclinedByUser(
  event: CalendarExternalEvent,
  userEmail?: string,
): boolean {
  if (!userEmail) return false
  const me = event.attendees?.find(a => a.email?.toLowerCase() === userEmail)
  return me?.responseStatus === 'declined'
}
